import axios from 'axios';
import { logout } from '../redux/actions/authActions';
import store from "../redux/store";
import userPool from "./userPool";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getAuthHeader = async () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
        try {
            const session = await new Promise((resolve, reject) => {
                cognitoUser.getSession((err, session) => err ? reject(err) : resolve(session));
            });
            const accessToken = session.getAccessToken().getJwtToken();
            return { 'Authorization': `Bearer ${accessToken}` };
        } catch (error) {
            console.error('Error refreshing access token:', error);
            return {};
        }
    }
    return {};
};


/**
 * Запрос, который переживает уход со страницы.
 *
 * `keepalive` — единственный способ дослать накопленное при закрытии вкладки: обычный
 * запрос браузер убивает вместе с документом, а `sendBeacon` не умеет заголовков, то
 * есть не пройдёт авторизацию. Ответ никого не интересует — страницы, которая его
 * прочитала бы, уже нет.
 */
export const authKeepAlive = async (url, data) => {
    const headers = await getAuthHeader().catch(() => ({}));
    const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true,
    });
    if (!response.ok) {
        // fetch отклоняется только на обрыве связи, а 4xx и 5xx возвращает как обычный
        // ответ. Без этой проверки вызывающий считал бы отказ сервера успехом — и
        // отличался бы поведением от обычного запроса, который в том же случае бросает.
        const error = new Error(`Request failed with status ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response;
};

const authAxios = async (url, options = {}, responseType = 'json') => {
    const headers = await getAuthHeader().catch(() => ({}));
    const fullUrl = `${API_BASE_URL}${url}`;

    let finalHeaders = {
        ...headers,
        ...options.headers,
    };

    if (options.data && !(options.data instanceof FormData)) {
        finalHeaders['Content-Type'] = 'application/json';
    }

    try {
        const response = await axios({
            url: fullUrl,
            method: options.method || 'GET',
            headers: finalHeaders,
            data: options.data,
            responseType: responseType,
        });

        if (response.status === 204 || !response.data) {
            return null;
        }

        return response.data;
    } catch (error) {
        if (error.response?.status === 401) {
            store.dispatch(logout());
        }

        let payload = error.response?.data;
        // Ошибка blob-запроса приходит blob'ом, хотя внутри обычный JSON сервера:
        // не раскрыв его, мы потеряли бы машинный код (QUOTA_EXCEEDED, PLAN_REQUIRED),
        // по которому интерфейс выбирает, что сказать пользователю.
        if (payload instanceof Blob) {
            try {
                payload = JSON.parse(await payload.text());
            } catch {
                payload = null;
            }
        }
        const errorMessage = payload?.errorMessage || payload || error.message;
        console.error('Axios error:', errorMessage);
        const thrown = new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        if (payload?.code) {
            thrown.code = payload.code;
        }
        // Код ответа, если он был. Пусто значит, что ответа не было вовсе — запрос не
        // дошёл. Разница важна тому, кто решает, повторять ли отправку: сервер, который
        // ответил, запрос уже видел, и повтор применил бы его второй раз.
        thrown.status = error.response?.status;
        throw thrown;
    }
};

export default authAxios;
